import { AbstractSerializer } from '../../../common/AbstractSerializer';
import type { BinaryReader } from '../../../common/BinaryReader';
import Header from '../../../common/Header.js';
import { initWorker } from '../../../common/WorkerUtility.js';
import type { LayerRecord, SerializerResolver } from '@visualizer/types';

export default class MltSerializer extends AbstractSerializer<void> {
    public readonly version: string;
    private readonly fileVersion: string;

    constructor(version: string) {
        super();
        this.version = version;
        this.fileVersion = version;
    }

    public async read(reader: BinaryReader, layer: LayerRecord, sm: SerializerResolver): Promise<void> {
        const worker = await initWorker();

        return new Promise((resolve, reject) => {
            worker.onmessage = (e: MessageEvent<Record<string, any>>) => {
                if (e.data.pointsPool != null) {
                    layer.raysHandler?.init?.(e.data.pointsPool, layer, 1);
                }

                if (e.data.pointsValues != null) {
                    layer.raysHandler?.addMultiValues?.(e.data.pointsValues, e.data.min, e.data.max);
                }

                if (e.data.indexA != null && e.data.indexB != null) {
                    const { spID, indexA, indexB, value, mlID } = e.data;
                    layer.raysHandler?.addMultiLine?.(0, spID, mlID, indexA, indexB, value);
                }

                if (e.data.finished === true) {
                    console.log('Rays have been loaded in all the Frames and ready to use');
                    console.log('Worker finishes here his job');
                    console.log(layer.raysHandler);
                    cleanup();
                    resolve();
                    return;
                }

                if (e.data?.error) {
                    cleanup();
                    reject(new Error(e.data.error || 'Worker error'));
                }
            };

            worker.onerror = (e: ErrorEvent) => {
                cleanup();
                reject(e.error || new Error(e.message || 'Worker script error'));
            };

            const cleanup = () => {
                try {
                    worker.terminate();
                } catch {}
            };

            const data = reader.getData();
            const headerSerializer = sm.findSerializer(this.fileVersion, Header, 'binary');
            worker.postMessage({ data, kind: 'mlt', serializerVersion: headerSerializer.serializerVersion }, [data]);
        });
    }
}

export function loadMLT(generalLoader: BinaryReader, sensorPointsN: number, postMessage: (payload: Record<string, unknown>) => void): void {
    generalLoader.readUInt32();
    const rayCount = generalLoader.readUInt32();

    const pointsPool: unknown[] = [];
    for (let i = 0; i < rayCount; i++) {
        pointsPool.push(generalLoader.readVec3());
    }

    postMessage({ pointsPool });

    const readNext = generalLoader.readBoolean();

    const pointsValues: number[] = [];
    if (readNext) {
        let min = Infinity;
        let max = -Infinity;
        for (let i = 0; i < rayCount; i++) {
            const value = generalLoader.readFloat32();
            if (value < min) min = value;
            if (value > max) max = value;
            pointsValues.push(value);
        }
        postMessage({ pointsValues, min, max });
    }

    for (let n = 0; n < sensorPointsN; n++) {
        const multiLineCount = generalLoader.readUInt32();
        for (let m = 0; m < multiLineCount; m++) {
            let multiLineValue = -1;
            if (!readNext) {
                multiLineValue = generalLoader.readFloat32();
            }
            const indexA = generalLoader.readUInt32();
            const indexB = generalLoader.readUInt32();
            postMessage({ value: multiLineValue, mlID: m, spID: n, indexA, indexB });
        }
    }
}
